// ============================================================
// TILT — ENGINE  (the game layer)
// ------------------------------------------------------------
// The solver (physics/World) knows about circles and segments.
// It knows NOTHING about score, charge, tilt, powers, or winning.
//
// This file is the membrane between them. It:
//   • owns the World and the accumulator (fixed-step discipline)
//   • drains solver events and turns tags into GAME MEANING
//   • runs the charge economy
//   • runs tilt
//   • casts powers
//   • tracks objectives (score tables AND trial tables, same code)
//   • emits a flat, serializable snapshot for React to render
//
// ── THE SEPARATION LAW ───────────────────────────────────────
// React NEVER touches the World. React reads `engine.snapshot()`
// and calls `engine.press()/engine.cast()`. That's the entire
// surface. Why: a React re-render must never be able to perturb
// physics, or the sim stops being deterministic and PvP dies.
// The engine is a plain JS object living in a useRef. It is not
// state. It is a machine that React watches.
//
// ── THE ACCUMULATOR ──────────────────────────────────────────
// requestAnimationFrame gives us a variable dt (16.6ms on a good
// day, 40ms when the browser hiccups, 6.9ms on a 144Hz panel).
// We bank that time and spend it in exact FIXED_DT chunks. Any
// remainder carries to the next frame. Result: identical physics
// on every machine, forever.
//   MAX_FRAME_TIME caps the bank so a tab that was backgrounded
//   for 30 seconds doesn't try to simulate 3600 steps at once and
//   freeze the page ("the spiral of death").
// ============================================================

import { World, FIXED_DT, Flipper, makeBall } from "./physics/World";
import { POWERS, getPower, chargeFor, CHARGE_MAX, validateLoadout } from "./Powers";
import { len, clamp } from "./physics/Vec";
import { lytFor } from "./Aenergy";
import {
  BOSS_MOVES, GUARDIAN_MOVES, applyBossMove, applyGuardianMove,
  bossAiTick, validateBossDef,
} from "./BossKit";

const MAX_FRAME_TIME = 0.25;      // never bank more than 250ms
const TILT_MAX       = 3;         // warnings before the table dies
const TILT_DECAY     = 6.0;       // seconds for one warning to fade
const DRAIN_GRACE    = 0.35;      // s before a drained ball is confirmed
const BALL_SAVE_TIME = 5.0;       // s of free ball-save after launch

export const PHASE = {
  READY:    "ready",       // ball in the plunger lane
  LIVE:     "live",        // ball in play
  DRAINED:  "drained",     // between balls
  TILTED:   "tilted",      // table dead this ball
  CLEARED:  "cleared",     // objective met (trial tables)
  OVER:     "over",        // no balls left
};

export class TiltEngine {
  constructor(table, opts = {}) {
    this.table   = table;
    this.seed    = opts.seed ?? (table.seed ?? 12345);
    this.owner   = opts.owner ?? "p1";
    this.loadout = validateLoadout(opts.loadout || table.loadout);

    this.world = new World({
      gravity: table.gravity ?? 420,
      damping: table.damping ?? 0.9992,
      seed:    this.seed,
    });

    // ── Flippers ──────────────────────────────────────────────
    this.flippers = (table.flippers || []).map((f) => new Flipper(f).mount(this.world));
    this.flipL = this.flippers.filter((f) => f.side === "L");
    this.flipR = this.flippers.filter((f) => f.side === "R");

    // ── Table geometry ────────────────────────────────────────
    for (const c of table.build(this.world)) this.world.addCollider(c);
    for (const f of (table.fields || [])) this.world.addField(f);

    // ── Game state ────────────────────────────────────────────
    this.score      = 0;
    this.charge     = table.startCharge ?? 25;
    this.ballsLeft  = table.balls ?? 3;
    this.ballNum    = 1;
    this.combo      = 0;
    this.comboTimer = 0;
    this.multiplier = 1;
    this.phase      = PHASE.READY;
    this.tilt       = 0;
    this.tiltTimer  = 0;
    this.ballSave   = 0;
    this.plunger    = 0;              // 0..1 charge on the launcher
    this.elapsed    = 0;
    this.acc        = 0;              // the accumulator
    this._idc       = 0;

    // Per-ball / per-round bookkeeping
    this.tempColliders = [];          // { id, ttl }
    this.timers        = [];          // { t, fn }
    this.fx            = [];          // render flourishes (drained by UI)
    this.log           = [];          // recent events for the HUD ticker
    this.aiming        = null;        // power id awaiting a point
    this.cooldowns     = {};          // per-power soft cooldown (anti-spam)

    // Objective state — the SAME machinery serves score tables and
    // trial tables. A score table's objective is "survive & score";
    // a trial table's is "light all four nodes." Both are just
    // predicates over `this.objectives`.
    this.objectives = {};
    for (const o of (table.objectives || [])) {
      this.objectives[o.id] = { ...o, done: false, progress: 0 };
    }
    this.cleared = false;

    // Incoming sabotage queue (filled by the SabotageBus in PvP,
    // or by the NPC opponent in single-player Gauntlet).
    this.incoming = [];

    // ── THE BOSS — a BEING, if the table declares one ─────────
    // The table owns the boss's IDENTITY (name, stats, skins, its
    // menace ladder); the engine owns its PHYSIOLOGY (Anima, Lyf,
    // the Fracture, the fell). Same split as objectives: the
    // machinery is generic so PvP and solo run the SAME monster.
    //
    //   bossDriven: "ai"     — the boss drives itself (solo, and
    //                          NPC duels, where it IS the NPC's
    //                          avatar acting on its own).
    //               "remote" — a human opponent drives it over
    //                          the SabotageBus (real PvP). The AI
    //                          is OFF; every swing was paid for.
    this.boss = null;
    this.flipperFracture = 0;      // s of FRACTURE left on YOUR flippers
    this.bossDriven = opts.bossDriven || "ai";
    if (table.boss) this._initBoss(table.boss);

    // Live callback hooks the game screen can subscribe to.
    this.onEvent = opts.onEvent || (() => {});

    this.spawnBall_ready();
  }

  nextId() { return ++this._idc; }

  // ── Boss lifecycle ──────────────────────────────────────────
  _initBoss(def) {
    const v = validateBossDef(def);
    if (!v.valid) {
      // A crash you can read is a gift — same law as the table
      // registry's boot check. A monster with a typo'd move id
      // would otherwise silently never fight.
      throw new Error(`[TILT] bad boss on "${this.table.id}": ${v.violations.join("; ")}`);
    }
    const skin = {};
    for (const m of def.moves || []) {
      skin[m.id] = m.name || BOSS_MOVES[m.id].name;
    }
    // The AI's own seeded generator — NEVER world.rng. The AI
    // beats on frame time; drawing from the physics stream here
    // would perturb trajectories differently per frame rate.
    let rs = ((this.seed ^ 0x9e3779b9) + 0x6d2b79f5) >>> 0;
    const rng = () => {
      rs = (rs * 1664525 + 1013904223) >>> 0;
      return rs / 4294967296;
    };
    this.boss = {
      name:  def.name,
      title: def.title || "",
      anima: def.anima, animaMax: def.anima,   // its LIFE. persists across drains.
      lyf:   def.lyf,   lyfMax:   def.lyf,     // its GUARD. resets on drain (Lyf Cycle).
      fractured: def.lyf === 0,                // R29: 0 Lyf = permanently Fractured
      felled: false,
      khao: 0, khaoMax: 100,                   // the AI's meter (solo only)
      moves: (def.moves || []).map((m) => m.id),
      duelKit: def.duelKit || [],
      skin,
      _beat: 0,
      _rng: rng,
    };
  }

  /**
   * A physical STRIKE on the boss — the table calls this from its
   * own sensor gate (the Cirque's heart, open only at MELTDOWN).
   *
   * The Lyf Cycle, played out:
   *   guard up   → the strike dims LYF by your current Lyt (combo
   *                heat — you cannot break a guard by cradling).
   *                No overflow: cracking the wall IS the payout.
   *   guard down → FRACTURED for the rest of this ball. Strikes
   *                and Impera now dim ANIMA. Your drain re-forms
   *                the guard in full. Chip damage is worthless.
   */
  bossStrike(e) {
    const B = this.boss;
    if (!B || B.felled || this.phase !== PHASE.LIVE) return null;

    const overclocked = (e?.ball?.speedMul ?? 1) > 1;
    const lyt = lytFor(this.combo, overclocked);

    if (!B.fractured) {
      B.lyf = Math.max(0, B.lyf - lyt);
      if (B.lyf === 0) {
        B.fractured = true;
        this._push("mode", `⛓ ${B.name} FRACTURED — strike with your Impera`);
        this._fx({ fx: "mode", label: "FRACTURED" });
        this.onEvent({ type: "boss_fractured" });
      } else {
        this._push("hit", `◈ GUARD −${lyt}  (${B.lyf}/${B.lyfMax})`);
      }
      return { kind: "guard", lyt };
    }
    return this.dimBoss(lyt, "strike", e?.point);
  }

  /** The ONLY way boss Anima ever goes down. Fell fires exactly
   *  once; the table's onBossFelled hook gets the payout. */
  dimBoss(n, src = "strike", at = null) {
    const B = this.boss;
    if (!B || B.felled || n <= 0) return null;
    B.anima = Math.max(0, B.anima - n);
    this._push("hit", `☄ ${B.name} DIMS −${n}  (${B.anima}/${B.animaMax})`);
    if (at) this._fx({ fx: "pop", at: { ...at }, pts: 0, tag: "boss" });

    if (B.anima === 0) {
      B.felled = true;
      B.fractured = false;
      this.onEvent({ type: "boss_felled", by: src });
      if (this.table.onBossFelled) {
        try { this.table.onBossFelled(this); } catch (e2) {
          console.warn("[TILT] onBossFelled threw:", e2?.message);
        }
      }
    }
    return { kind: "dim", n, felled: B.felled };
  }

  /** A boss move against YOUR flippers. They release and refuse
   *  input until the Fracture expires. One second is an eternity. */
  fractureFlippers(t) {
    this.flipperFracture = Math.max(this.flipperFracture, t);
    for (const f of this.flippers) f.press(false);
  }

  // ── Ball lifecycle ──────────────────────────────────────────
  spawnBall_ready() {
    const L = this.table.launch;
    this.world.balls = [];
    const b = makeBall(L.x, L.y, { r: this.table.ballR ?? 7, id: this.nextId(), owner: this.owner });
    b.vel = { x: 0, y: 0 };
    b.gravityMul = 0;                 // held in the lane, no gravity
    this.world.addBall(b);
    this.phase   = PHASE.READY;
    this.plunger = 0;
    this.combo   = 0;
    this.multiplier = 1;
    return b;
  }

  spawnBall(x, y, vx, vy, o = {}) {
    const b = makeBall(x, y, {
      r: this.table.ballR ?? 7, vx, vy,
      id: this.nextId(), owner: o.owner ?? this.owner,
    });
    this.world.addBall(b);
    return b;
  }

  /** The primary ball — the one powers target by default. */
  primary() {
    const live = this.world.balls.filter((b) => b.alive);
    if (live.length === 0) return null;
    // The one with the most kinetic energy is the one the player is
    // watching. In multiball that's almost always the right guess.
    return live.reduce((a, b) => (len(b.vel) > len(a.vel) ? b : a));
  }
  liveBalls() { return this.world.balls.filter((b) => b.alive); }

  // ── Plunger ─────────────────────────────────────────────────
  pullPlunger(dt) {
    if (this.phase !== PHASE.READY) return;
    this.plunger = clamp(this.plunger + dt * 1.15, 0, 1);
  }
  release() {
    if (this.phase !== PHASE.READY) return;
    const b = this.world.balls[0];
    if (!b) return;

    // ── THE PLUNGER STEERS. IT DOES NOT DECIDE SURVIVAL. ──────
    //
    // A launch that cannot physically reach the playfield is not a
    // "weak shot" — it is a BROKEN TURN. The ball rides up the lane,
    // stalls, and falls straight back into the drain having touched
    // nothing. The player loses a ball to a UI element they can't
    // see. That is never acceptable, at any pull strength.
    //
    // So the vertical launch is a CONSTANT: every ball, every time,
    // clears the lane and reaches the deflector with speed to spare.
    // What the plunger actually controls is the ANGLE — a light pull
    // feeds a soft entry that dribbles down the right; a hard pull
    // rockets around the arch and comes in from the far left. Same
    // survival, different shot. That's a real choice instead of a
    // trap.
    //
    // (The old model scaled the *speed*, so a light pull simply
    // died. The headless sim caught it — a stalled launch and a good
    // one look identical in a screenshot. This is why the sim exists.)
    const p = clamp(this.plunger, 0, 1);
    const base = this.table.launchPower ?? 780;   // ALWAYS clears the lane
    const power = base + 90 * p;                  // the top-end is a bonus, not a rescue
    b.gravityMul = 1;
    b.vel = { x: (this.table.launchDir?.x ?? 0) * power,
              y: (this.table.launchDir?.y ?? -1) * power };
    // Every launched ball is a FRESH ball as far as sensors are
    // concerned. Without this, a ball that drained inside a sensor
    // (the drain IS a sensor) respawns still latched into it, and
    // that sensor stays dead for the rest of the ball's life.
    b.inSensor = {};
    this.phase    = PHASE.LIVE;
    this.plunger  = 0;
    this.ballSave = BALL_SAVE_TIME;
    this._push("launch", `Ball ${this.ballNum}`);
    // D6: the lifetime-stats slots (`stats.launches` et al.) had
    // schema, rules, and a tested delta — and read 0 forever,
    // because nothing EMITTED a launch. Drains, powers, and
    // sabotages already had events; this was the one hole.
    this.onEvent({ type: "launch", ball: this.ballNum });
  }

  // ── Flippers ────────────────────────────────────────────────
  press(side, down) {
    if (this.phase === PHASE.TILTED) return;   // tilt kills the flippers
    if (this.flipperFracture > 0) return;      // FRACTURE — they refuse you
    const set = side === "L" ? this.flipL : this.flipR;
    for (const f of set) f.press(down);
  }

  // ── Powers ──────────────────────────────────────────────────
  /** Begin aiming a point-targeted power. UI enters aim mode. */
  beginAim(powerId) {
    const p = getPower(powerId);
    if (!p || p.target !== "point") return false;
    if (!this.canCast(powerId)) return false;
    this.aiming = powerId;
    return true;
  }
  cancelAim() { this.aiming = null; }

  canCast(powerId) {
    const p = getPower(powerId);
    if (!p) return false;
    if (p.id !== "nudge" && !this.loadout.includes(p.id)) return false;
    if (this.charge < p.cost) return false;
    if (this.phase !== PHASE.LIVE) return false;
    if ((this.cooldowns[p.id] || 0) > 0) return false;
    return true;
  }

  /**
   * Cast a power. `arg` is a table-space point for "point" powers,
   * or a direction vector for nudge.
   *
   * Returns the fx descriptor (or null). NEVER throws — a power
   * that can't fire is a no-op, not an exception. A physics loop
   * must not be able to die from a bad UI tap.
   */
  cast(powerId, arg = null) {
    const p = getPower(powerId);
    if (!p) return null;
    if (!this.canCast(powerId)) return null;

    const ctx = {
      world: this.world,
      ball:  this.primary(),
      balls: this.liveBalls(),
      point: p.target === "point" ? arg : null,
      dir:   powerId === "nudge" ? arg : null,
      table: this.table,
      engine: this,
      rng:   this.world.rng,
    };

    let fx = null;
    try { fx = p.apply(ctx); } catch (e) {
      console.warn(`[TILT] power "${powerId}" threw:`, e?.message);
      return null;
    }
    if (!fx) return null;

    this.charge = Math.max(0, this.charge - p.cost);
    this.cooldowns[p.id] = 0.4;        // anti-double-tap, not a real cooldown
    this.aiming = null;
    this._fx(fx);
    this._push("power", p.name);
    this.onEvent({ type: "power", power: p.id, owner: this.owner });

    // ── THE IMPERA STRIKE ─────────────────────────────────────
    // While the boss is FRACTURED, every zone-bound Impera you
    // cast lands on it as a metaphysical blow — level × 2 Anima.
    // The ball opens the door; the Impera walk through it. (Nudge
    // is excluded: zone null, the ancestor, and free things don't
    // dim beings.)
    if (this.boss?.fractured && !this.boss.felled &&
        p.kind === "impera" && p.zone) {
      this.dimBoss(p.level * 2, `impera:${p.id}`);
    }
    return fx;
  }

  /**
   * Apply an INCOMING sabotage — a power cast at THIS table by an
   * opponent. Same power registry, the `onEnemy` branch. Costs this
   * table nothing (the caster already paid). This is the one entry
   * point the network layer touches.
   */
  receiveSabotage(powerId, arg = null, from = "?") {
    // ── MONSTER VERBS ride the same wire as sabotages ─────────
    // In a duel the boss on this table is the OPPONENT's avatar:
    // their monster-attack spends arrive here as BOSS_MOVES, and
    // their BULWARK/MEND arrive here as GUARDIAN_MOVES (their
    // monster lives on THIS table, so tending it lands here too).
    // Both funnel through BossKit's one gate — R-UNIVERSAL.
    if (BOSS_MOVES[powerId])     return applyBossMove(this, powerId, from);
    if (GUARDIAN_MOVES[powerId]) return applyGuardianMove(this, powerId, from);

    const p = getPower(powerId);
    if (!p || !p.sabotage || !p.onEnemy) return null;
    if (this.phase !== PHASE.LIVE) {
      // Queue it — a sabotage that lands while the table is between
      // balls would be wasted. It waits for the next launch. Rude,
      // but not WASTED. (Sabotage should always land eventually or
      // the sender feels cheated by netcode.)
      this.incoming.push({ powerId, arg, from });
      return null;
    }
    const ctx = {
      world: this.world, ball: this.primary(), balls: this.liveBalls(),
      point: arg, table: this.table, engine: this, rng: this.world.rng,
    };
    let fx = null;
    try { fx = p.onEnemy(ctx); } catch (e) {
      console.warn(`[TILT] sabotage "${powerId}" threw:`, e?.message);
      return null;
    }
    if (!fx) return null;
    this._fx({ ...fx, hostile: true });
    this._push("sabotage", `${from}: ${p.name}!`);
    this.onEvent({ type: "sabotaged", power: p.id, from });
    return fx;
  }

  _flushIncoming() {
    if (!this.incoming.length || this.phase !== PHASE.LIVE) return;
    const q = this.incoming;
    this.incoming = [];
    for (const s of q) this.receiveSabotage(s.powerId, s.arg, s.from);
  }

  /** Pay charge for an off-registry action (the duel's monster
   *  rail). Returns false untouched if you can't afford it. */
  spend(n) {
    if (this.charge < n || this.phase === PHASE.OVER) return false;
    this.charge = Math.max(0, this.charge - n);
    return true;
  }

  // ── Temp geometry & timers (power support) ──────────────────
  addTempCollider(c, ttl) {
    this.world.addCollider(c);
    this.tempColliders.push({ id: c.id, ttl });
  }
  addTimer(t, fn) { this.timers.push({ t, fn }); }

  // ── Tilt ────────────────────────────────────────────────────
  addTiltWarning() {
    this.tilt += 1;
    this.tiltTimer = TILT_DECAY;
    if (this.tilt >= TILT_MAX) {
      this.phase = PHASE.TILTED;
      this._push("tilt", "TILT — flippers dead");
      this.onEvent({ type: "tilt" });
      // Flippers go limp; the ball drains on its own. The table is
      // not "reset" — you WATCH it die. That's the punishment.
      for (const f of this.flippers) f.press(false);
    } else {
      this._push("warn", `Tilt warning ${this.tilt}/${TILT_MAX}`);
    }
  }

  // ── Scoring & charge ────────────────────────────────────────
  _award(tag, ball, speed) {
    const base = this.table.scoreTable?.[tag] ?? DEFAULT_SCORES[tag] ?? 0;
    if (base === 0) return;

    // Speed bonus — a fast hit is worth more. This is what makes
    // Overclock pay for itself and what makes cradling boring.
    const spd = clamp(speed / 400, 0, 2);
    const om  = ball?.speedMul ?? 1;

    // Combo — consecutive scoring hits inside the combo window.
    this.combo += 1;
    this.comboTimer = 2.2;
    const cm = 1 + Math.min(this.combo, 12) * 0.1;

    const pts = Math.round(base * this.multiplier * cm * om * (1 + spd * 0.35));
    this.score += pts;
    return pts;
  }

  _charge(tag) {
    const c = chargeFor(tag, this.table);
    if (!c) return;
    this.charge = clamp(this.charge + c, 0, CHARGE_MAX);
  }

  // ── Objectives ──────────────────────────────────────────────
  // A trial table declares objectives like:
  //   { id:"nodes", kind:"collect", tags:["node"], need:4 }
  //   { id:"vault", kind:"reach",   tags:["vault_gate"] }
  //   { id:"speed", kind:"score",   need:50000 }
  // The engine evaluates them uniformly. A score table can declare
  // ZERO objectives and everything below is a no-op.
  _objective(tag) {
    let changed = false;
    for (const o of Object.values(this.objectives)) {
      if (o.done) continue;
      if (o.kind === "collect" && o.tags?.includes(tag)) {
        // Collect: each DISTINCT id counts once.
        o.seen = o.seen || new Set();
        o.progress = o.seen.size;
        changed = true;
      } else if (o.kind === "reach" && o.tags?.includes(tag)) {
        o.done = true; o.progress = 1;
        changed = true;
      }
    }
    if (changed) this._checkCleared();
  }

  _objectiveHit(tag, id) {
    for (const o of Object.values(this.objectives)) {
      if (o.done || o.kind !== "collect" || !o.tags?.includes(tag)) continue;
      o.seen = o.seen || new Set();
      if (!o.seen.has(id)) {
        o.seen.add(id);
        o.progress = o.seen.size;
        if (o.progress >= (o.need ?? 1)) o.done = true;
      }
    }
    this._checkCleared();
  }

  _checkCleared() {
    for (const o of Object.values(this.objectives)) {
      if (o.kind === "score") o.done = this.score >= (o.need ?? Infinity);
    }
    const all = Object.values(this.objectives);
    if (all.length === 0) return;
    if (all.every((o) => o.done) && !this.cleared) {
      this.cleared = true;
      this.phase   = PHASE.CLEARED;
      this._push("clear", "TABLE CLEARED");
      this.onEvent({ type: "cleared", score: this.score });
    }
  }

  // ── Event translation — solver tags → game meaning ───────────
  _handle(e) {
    const tag = e.tag;

    // DRAIN — the only tag the engine treats as structural.
    if (tag === "drain") {
      this._drain(e.ball);
      return;
    }

    if (e.type === "sensor") {
      // Sensors: rollovers, lit lanes, gates, objective nodes.
      if (this.table.onSensor) this.table.onSensor(this, e);
      this._award(tag, e.ball, e.speed);
      this._charge(tag);
      this._objectiveHit(tag, e.id);
      return;
    }

    // Solid hits: bumpers, targets, slings, walls.
    if (this.table.onHit) this.table.onHit(this, e);
    const pts = this._award(tag, e.ball, e.speed);
    this._charge(tag);
    if (tag !== "wall" && tag !== "flipper" && tag !== "flipper_post") {
      this._objectiveHit(tag, e.id);
    }
    if (pts) {
      this._fx({ fx: "pop", at: e.point, pts, tag });
    }
  }

  _drain(ball) {
    if (!ball || !ball.alive) return;
    ball.alive = false;

    // Ball save — a free re-serve early in the ball. Softens the
    // brutal "launched, hit nothing, drained in 4 seconds" opener.
    if (this.ballSave > 0 && this.liveBalls().length === 0) {
      this._push("save", "BALL SAVED");
      this.ballSave = 0;
      this.spawnBall_ready();
      return;
    }

    this._charge("drain");
    this._fx({ fx: "drain" });

    // Multiball: only the LAST ball out ends the round.
    if (this.liveBalls().length > 0) {
      this._push("lost", "Ball lost — one still live!");
      return;
    }

    this.ballsLeft -= 1;
    this.combo = 0;
    this.multiplier = 1;

    // ★ THE LYF CYCLE (R27/28) — the boss's guard restores IN
    //   FULL on your drain. Chip damage is worthless: you break
    //   through THIS ball or you start the wall over. Its ANIMA
    //   is the Soul side of the line — the wound persists.
    if (this.boss && !this.boss.felled) {
      this.boss.lyf = this.boss.lyfMax;
      this.boss.fractured = this.boss.lyfMax === 0;   // R29 stays true
    }

    this.onEvent({ type: "drain", ballsLeft: this.ballsLeft });

    if (this.ballsLeft <= 0) {
      this.phase = PHASE.OVER;
      this._push("over", "GAME OVER");
      this.onEvent({ type: "over", score: this.score });
      return;
    }

    this.phase = PHASE.DRAINED;
    this.ballNum += 1;
    // Clear tilt on a fresh ball — tilt is per-ball, not per-game.
    this.tilt = 0;
    // Temp geometry does NOT survive a drain. Your anchors die with
    // your ball. (Otherwise you'd farm anchors across balls and the
    // table would silt up into a pachinko machine.)
    this._clearTemp();
    setTimeout(() => {
      if (this.phase === PHASE.DRAINED) this.spawnBall_ready();
    }, 700);
  }

  _clearTemp() {
    for (const t of this.tempColliders) this.world.removeCollider(t.id);
    this.tempColliders = [];
    this.world.fields = this.world.fields.filter((f) => f.ttl === undefined);
  }

  // ── THE FRAME ───────────────────────────────────────────────
  // Called once per requestAnimationFrame with real elapsed time.
  // Everything deterministic happens inside the fixed-step loop;
  // everything cosmetic/temporal happens once, outside it.
  tick(dt) {
    dt = Math.min(dt, MAX_FRAME_TIME);
    this.elapsed += dt;

    // Timers, cooldowns, decay — real-time, once per frame.
    for (const t of this.timers) t.t -= dt;
    for (const t of this.timers) if (t.t <= 0) { try { t.fn(); } catch {} }
    this.timers = this.timers.filter((t) => t.t > 0);

    for (const k of Object.keys(this.cooldowns)) {
      this.cooldowns[k] = Math.max(0, this.cooldowns[k] - dt);
    }

    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) this.combo = 0;
    }
    if (this.ballSave > 0) this.ballSave = Math.max(0, this.ballSave - dt);

    if (this.tiltTimer > 0) {
      this.tiltTimer -= dt;
      if (this.tiltTimer <= 0 && this.tilt > 0 && this.phase !== PHASE.TILTED) {
        this.tilt -= 1;
        this.tiltTimer = this.tilt > 0 ? TILT_DECAY : 0;
      }
    }

    // Temp colliders expire in real time.
    for (const t of this.tempColliders) t.ttl -= dt;
    const dead = this.tempColliders.filter((t) => t.ttl <= 0);
    for (const t of dead) this.world.removeCollider(t.id);
    this.tempColliders = this.tempColliders.filter((t) => t.ttl > 0);

    this.world.tickFields(dt);
    this._flushIncoming();

    // ── The boss's clock ──────────────────────────────────────
    if (this.flipperFracture > 0) {
      this.flipperFracture = Math.max(0, this.flipperFracture - dt);
    }
    // The AI drives the monster only when no human does. In PvP
    // (bossDriven: "remote") every swing is an opponent's spend.
    if (this.boss && this.bossDriven === "ai") bossAiTick(this, dt);

    // Table's own per-frame hook (mode timers, lit-lane rotation).
    if (this.table.onTick) this.table.onTick(this, dt);

    if (this.phase === PHASE.OVER || this.phase === PHASE.CLEARED) return;

    // ── THE FIXED-STEP LOOP (LAW 1) ───────────────────────────
    this.acc += dt;
    let steps = 0;
    while (this.acc >= FIXED_DT && steps < 12) {
      for (const f of this.flippers) f.update(FIXED_DT);
      this.world.step();
      for (const e of this.world.drainEvents()) this._handle(e);
      this.acc -= FIXED_DT;
      steps++;
    }
    // Guard: if we blew the step budget (a truly awful frame), drop
    // the backlog rather than spiral. Physics jitters once; the tab
    // does not freeze.
    if (steps >= 12) this.acc = 0;
  }

  // ── Render surface ──────────────────────────────────────────
  _fx(f) {
    this.fx.push({ ...f, t: this.elapsed, key: this.nextId() });
    if (this.fx.length > 40) this.fx.shift();
  }
  _push(kind, msg) {
    this.log.unshift({ kind, msg, t: this.elapsed, key: this.nextId() });
    if (this.log.length > 6) this.log.pop();
  }
  drainFx() { const f = this.fx; this.fx = []; return f; }

  /**
   * A flat, plain-object snapshot for React. Called once per frame.
   * NOTHING in here is a live reference into the World — the render
   * layer must never be able to mutate physics by accident.
   */
  snapshot() {
    return {
      score: this.score,
      charge: this.charge,
      chargeMax: CHARGE_MAX,
      ballsLeft: this.ballsLeft,
      ballNum: this.ballNum,
      combo: this.combo,
      multiplier: this.multiplier,
      phase: this.phase,
      tilt: this.tilt,
      tiltMax: TILT_MAX,
      ballSave: this.ballSave,
      plunger: this.plunger,
      aiming: this.aiming,
      cleared: this.cleared,
      flipperFracture: this.flipperFracture,
      boss: this.boss ? {
        name: this.boss.name, title: this.boss.title,
        anima: this.boss.anima, animaMax: this.boss.animaMax,
        lyf: this.boss.lyf, lyfMax: this.boss.lyfMax,
        fractured: this.boss.fractured, felled: this.boss.felled,
        khao: Math.round(this.boss.khao), khaoMax: this.boss.khaoMax,
        duelKit: this.boss.duelKit,
      } : null,
      loadout: this.loadout,
      cooldowns: { ...this.cooldowns },
      elapsed: this.elapsed,
      log: this.log.slice(),
      objectives: Object.values(this.objectives).map((o) => ({
        id: o.id, label: o.label, kind: o.kind,
        need: o.need ?? 1, progress: o.progress ?? 0, done: !!o.done,
      })),
      balls: this.world.balls
        .filter((b) => b.alive)
        .map((b) => ({
          x: b.p.x, y: b.p.y, r: b.r,
          vx: b.vel.x, vy: b.vel.y,
          frozen: b.frozen, ghost: b.ghost, speedMul: b.speedMul,
          trail: b.trail.slice(-10),
          id: b.id,
        })),
      flippers: this.flippers.map((f) => ({
        id: f.id, side: f.side,
        ax: f.pivot.x, ay: f.pivot.y,
        bx: f.tip.x,  by: f.tip.y,
        r: f.radius, pressed: f.pressed,
      })),
      // Temp geometry the renderer must draw (anchors, hostile bars)
      temps: this.tempColliders.map((t) => {
        const c = this.world.getCollider(t.id);
        if (!c) return null;
        return {
          id: c.id, tag: c.tag, ttl: t.ttl,
          ax: c.a?.x, ay: c.a?.y, bx: c.b?.x, by: c.b?.y,
        };
      }).filter(Boolean),
      fields: this.world.fields
        .filter((f) => f.kind === "magnet")
        .map((f) => ({ x: f.c.x, y: f.c.y, r: f.radius, ttl: f.ttl ?? 99 })),
      // Table-owned lamp state (lit targets, active modes)
      lamps: this.table.lamps ? this.table.lamps(this) : {},
    };
  }
}

// Fallback score values. A table overrides via `scoreTable`.
export const DEFAULT_SCORES = {
  bumper:    100,
  slingshot:  60,
  target:    250,
  target_lit: 750,
  ramp:     1200,
  orbit:     900,
  spinner:    35,
  jackpot:  5000,
  node:      500,
  anchor:      0,
  wall:        0,
  flipper:     0,
};

export default TiltEngine;
